(ns frontend.ui.ai-summary-configuration.model
  "Pure draft helpers shared by the deal modal and summary library."
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.ai.deal-one-pager-defaults :as defaults]
   [common.schema.entities.ai-summary-configuration.options :as options]))

(defn reference-id
  [reference]
  (:id reference))

(defn reference-ids
  [references]
  (->> references
       (keep reference-id)
       set))

(defn configuration-detail->draft
  [configuration]
  {:configuration-name (:configuration-name configuration)
   :configuration-description (:configuration-description configuration)
   :configuration-access-level (:configuration-access-level configuration)
   :use-access-ids (reference-ids (:use-access configuration))
   :applicable-pipeline-ids
   (set (or (:applicable-pipeline-ids configuration)
            (reference-ids (:applicable-pipelines configuration))))
   :default-pipeline-ids
   (set (or (:default-pipeline-ids configuration)
            (reference-ids (:default-pipelines configuration))))
   :summary-instructions (:summary-instructions configuration)
   :selected-data-sources (set (:selected-data-sources configuration))
   :style-guideline-source (:style-guideline-source configuration)
   :style-guideline-document-id
   (reference-id (:style-guideline-document configuration))
   :style-guideline-document-filename
   (get-in configuration [:style-guideline-document :label])
   :style-reference-url (:style-reference-url configuration)
   :document-template-id (reference-id (:document-template configuration))
   :document-template-filename (get-in configuration [:document-template :label])})

(defn blank-draft
  []
  {:configuration-name ""
   :configuration-description ""
   :configuration-access-level options/access-level-private
   :use-access-ids #{}
   :applicable-pipeline-ids #{}
   :default-pipeline-ids #{}
   :summary-instructions defaults/opportunity-brief-instructions
   :selected-data-sources (set options/default-data-sources)
   :style-guideline-source options/style-guideline-source-none
   :style-guideline-document-id nil
   :style-guideline-document-filename nil
   :style-reference-url nil
   :document-template-id nil
   :document-template-filename nil})

(defn copy-draft
  [draft copy-name]
  (assoc draft
         :configuration-name copy-name
         :configuration-access-level options/access-level-private
         :use-access-ids #{}
         :default-pipeline-ids #{}))

(defn copy-name-valid?
  [draft source-name]
  (let [copy-name (string/trim (or (:configuration-name draft) ""))
        source-name (string/trim (or source-name ""))]
    (and (not (string/blank? copy-name))
         (not= (string/lower-case copy-name)
               (string/lower-case source-name)))))

(defn draft-dirty?
  [draft saved-draft]
  (and draft
       (not= draft saved-draft)))

(defn draft-valid?
  [{:keys [configuration-name summary-instructions selected-data-sources
           style-guideline-source style-guideline-document-id
           style-reference-url applicable-pipeline-ids default-pipeline-ids]}]
  (and (not (string/blank? configuration-name))
       (not (string/blank? summary-instructions))
       (set/subset? (set selected-data-sources)
                    (set options/default-data-sources))
       (or (empty? applicable-pipeline-ids)
           (set/subset? (set default-pipeline-ids)
                        (set applicable-pipeline-ids)))
       (case style-guideline-source
         :ai-summary-configuration.style-guideline-source/none true
         :ai-summary-configuration.style-guideline-source/document
         (some? style-guideline-document-id)
         :ai-summary-configuration.style-guideline-source/url
         (not (string/blank? style-reference-url))
         false)))

(defn content-data
  [draft]
  {:ai-summary-configuration/configuration-name
   (:configuration-name draft)
   :ai-summary-configuration/configuration-description
   (:configuration-description draft)
   :ai-summary-configuration/summary-instructions
   (:summary-instructions draft)
   :ai-summary-configuration/selected-data-sources
   (:selected-data-sources draft)
   :ai-summary-configuration/style-guideline-source
   (:style-guideline-source draft)
   :ai-summary-configuration/style-guideline-document
   (:style-guideline-document-id draft)
   :ai-summary-configuration/style-reference-url
   (:style-reference-url draft)
   :ai-summary-configuration/document-template
   (:document-template-id draft)})

(defn management-data
  [draft]
  {:ai-summary-configuration/configuration-access-level
   (:configuration-access-level draft)
   :ai-summary-configuration/use-access
   (:use-access-ids draft)
   :ai-summary-configuration/applicable-pipelines
   (:applicable-pipeline-ids draft)
   :ai-summary-configuration/default-pipelines
   (:default-pipeline-ids draft)})

(defn update-data
  [draft]
  (merge (content-data draft)
         (management-data draft)))

(defn create-data
  [draft]
  (assoc (update-data draft)
         :entity/type :entity.type/ai-summary-configuration))

(defn private-copy-data
  [draft]
  (-> draft
      (assoc :configuration-access-level options/access-level-private
             :use-access-ids #{}
             :default-pipeline-ids #{})
      create-data))
